import { DEFAULT_FFT_SIZE, DEFAULT_SILENCE_THRESHOLD_DB } from '../../constants/audio.ts';
import { DEFAULT_SILENCE_DURATION_SECONDS } from '../../constants/audio.ts';
import { SilenceDetectionErrorType, SilenceDetectionOptions } from '../../types/audio.types.ts';

import { getSentryUtil } from '../monitoring/sentry.util.ts';

export class AudioSilenceDetectorUtil {
  private analyserNode: AnalyserNode | null = null;
  private audioContext: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private options: SilenceDetectionOptions;
  private silenceStartTime: number | null = null;
  private recordingStartTime: number | null = null;
  private animationFrameId: number | null = null;
  private dataArray: Float32Array | null = null;
  private isInitialized = false;
  // We added a minimum recording duration to ensure we give the user enough time to start speaking
  private readonly MINIMUM_RECORDING_DURATION = 1; // seconds

  private static _instance: AudioSilenceDetectorUtil;

  static get instance(): AudioSilenceDetectorUtil {
    if (!AudioSilenceDetectorUtil._instance) {
      AudioSilenceDetectorUtil._instance = new AudioSilenceDetectorUtil();
    }
    return AudioSilenceDetectorUtil._instance;
  }

  private constructor() {
    this.options = {
      silenceThreshold: DEFAULT_SILENCE_THRESHOLD_DB,
      silenceDuration: DEFAULT_SILENCE_DURATION_SECONDS,
      onSilenceDetected: undefined,
    };
  }

  get isBusy(): boolean {
    return this.isInitialized;
  }

  initialize(stream: MediaStream, options?: Partial<SilenceDetectionOptions>): void {
    if (this.isInitialized) {
      this.handleError(SilenceDetectionErrorType.ALREADY_INITIALIZED);
      return;
    }

    this.options = {
      ...this.options,
      ...options,
    };

    this.audioContext = new AudioContext();
    this.analyserNode = this.audioContext.createAnalyser();
    this.analyserNode.fftSize = DEFAULT_FFT_SIZE;
    this.source = this.audioContext.createMediaStreamSource(stream);
    this.source.connect(this.analyserNode);

    this.isInitialized = true;
    this.recordingStartTime = Date.now();
    this.startDetection();
  }

  stop(): void {
    if (this.animationFrameId !== null) {
      cancelAnimationFrame(this.animationFrameId);
      this.animationFrameId = null;
    }

    if (this.source) {
      this.source.disconnect();
      this.source = null;
    }

    if (this.audioContext) {
      this.audioContext.close();
      this.audioContext = null;
    }

    this.analyserNode = null;
    this.silenceStartTime = null;
    this.recordingStartTime = null;
    this.dataArray = null;
    this.isInitialized = false;
  }

  private startDetection(): void {
    if (!this.analyserNode) {
      return;
    }

    const bufferLength = this.analyserNode.frequencyBinCount;
    this.dataArray = new Float32Array(bufferLength);

    this.scheduleNextCheck();
  }

  private checkSilence(): void {
    if (!this.analyserNode || !this.dataArray) {
      this.handleError(SilenceDetectionErrorType.INITIALIZATION_ERROR);
      return;
    }

    // Check if minimum recording duration has elapsed
    const recordingDuration = this.recordingStartTime ? (Date.now() - this.recordingStartTime) / 1000 : 0;
    if (recordingDuration < this.MINIMUM_RECORDING_DURATION) {
      this.scheduleNextCheck();
      return;
    }

    this.analyserNode.getFloatTimeDomainData(this.dataArray);
    const db = this.computeSignalDecibels(this.dataArray);

    if (db < this.options.silenceThreshold) {
      this.markSilenceStartTime();
      if (this.checkSilenceDuration()) {
        this.options.onSilenceDetected?.();
        this.stop();
        return;
      }
    } else {
      this.resetSilenceStartTime();
    }
    this.scheduleNextCheck();
  }

  private markSilenceStartTime(): void {
    if (this.silenceStartTime === null) {
      this.silenceStartTime = Date.now();
    }
  }

  private checkSilenceDuration(): boolean {
    if (this.silenceStartTime) {
      return Date.now() - this.silenceStartTime >= this.options.silenceDuration * 1000;
    }
    return false;
  }

  private resetSilenceStartTime(): void {
    this.silenceStartTime = null;
  }

  private scheduleNextCheck(): void {
    this.animationFrameId = requestAnimationFrame(this.checkSilence.bind(this));
  }

  private computeSignalMagnitude(dataArray: Float32Array): number {
    // RMS (Root Mean Square) is a mathematical way to measure the average power or magnitude of an audio signal over time.
    // 1. **Square the Signal**: When you square the signal, you get the power at each point in time.
    // 2. **Summing Powers**: By summing these powers, you get a total power value.
    // 3. **Average Power**: To get the average power, you divide the total power by the number of samples.
    // 4. **Square Root**: Finally, to get the RMS value, you take the square root of the average power.
    const squaredSum = dataArray.reduce((sum, value) => sum + value * value, 0);
    return Math.sqrt(squaredSum / dataArray.length);
  }

  private computeSignalDecibels(dataArray: Float32Array): number {
    const magnitude = this.computeSignalMagnitude(dataArray);
    return 20 * Math.log10(magnitude);
  }

  private handleError(errorType: SilenceDetectionErrorType, error?: Error): void {
    getSentryUtil().trackError(error ?? new Error(errorType), {
      errorType,
    });
    this.stop();
  }
}

export const getAudioSilenceDetectorUtil = (): AudioSilenceDetectorUtil => AudioSilenceDetectorUtil.instance;
